import { SwitchableTabType } from './random'
import { DiscountTableDataType } from './discounts'
import { ElementStructure, FeatureState } from 'src/components/ui-customization/sub-components/thankyou-page/types'
import { AddressConfigType } from './merchantAttributes'

export enum CustomizeUIPageContextType {
  CUSTOMIZE_UI,
  EXPERIMENTS
}

export interface CustomizeUIContextType {
  isUpdated: boolean
  state: CustomizeUIStateType
  actions: {
    setTabListData: (tabList: SwitchableTabType[]) => void
    setCouponEditorData: (couponList: CouponEditorConfigType[]) => void
    setPaymentsMethodsData: (paymentModes: PaymentMethodConfigType[]) => void
    setBrandLogoURLData: (logoURL: string) => void
    setColorPaletteData: (colorPalette: ThemeColorPaletteType) => void
    setBannerConfigData: (bannerConfig: BannerConfigType, type: CheckoutPageType) => void
    setButtonCTATextData: (CTAText: string, type: CTAButtonPageType) => void
    setCouponVisibilityConfigData: (couponVisibilityConfig: CouponVisibilityConfigType, type: CheckoutPageType) => void
    setDeliveryInfoConfigData: (deliveryInfoConfig: DeliveryInfoConfigType) => void
    setTrustBadgesConfigData: (trustBadgesConfig: TrustBadgesConfigType) => void
    setOrderSummaryConfigData: (orderSummaryConfig: OrderSummaryConfigType) => void
    setBankOffersConfigData: (showBankOffers: boolean) => void
    reFetchCouponEditorData: () => void
    saveChanges: () => void
    discardChanges: () => void
    setETDProvidersData: (etdProviders: EtdProviderType[]) => void
    setThankYouPageFeatureState: (featureState: FeatureState) => void
    setMarketingFlagConfigData: (marketingFlagConfig: MarketingFlagConfigType) => void
    setDialogDisplayConfigData: (dialogs: DialogDisplayConfigType[]) => void
  }
}

export type EtdProviderType = {
  etd_provider: string
  is_configured: boolean
  metadata: {
    enabled: boolean
  }
}

export enum CustomizeUIActionSetType {
  SET_TAB_LIST_DATA,
  SET_COUPON_EDITOR_DATA,
  SET_PAYMENTS_METHODS_DATA,
  SET_BRAND_LOGO_URL_DATA,
  SET_COLOR_PALETTE_DATA,
  SET_BANNER_CONFIG_DATA,
  SET_BUTTON_CTA_TEXT_DATA,
  SET_COUPON_VISIBILITY_CONFIG_DATA,
  SET_DELIVERY_INFO_CONFIG_DATA,
  SET_TRUST_BADGES_CONFIG_DATA,
  SET_ORDER_SUMMARY_CONFIG,
  SET_BANK_OFFERS_CONFIG_DATA,
  SET_COMPLETE_CONFIG,
  SET_ETD_PROVIDERS_DATA,
  SET_THANK_YOU_PAGE_FEATURE_STATE,
  SET_ADDRESS_CONFIG_DATA,
  SET_MARKETING_FLAG_CONFIG_DATA,
  SET_DIALOG_DISPLAY_CONFIG_DATA
}

export type CustomizeUIActionType =
  | {
      type: CustomizeUIActionSetType.SET_TAB_LIST_DATA
      payload: SwitchableTabType[]
    }
  | {
      type: CustomizeUIActionSetType.SET_COUPON_EDITOR_DATA
      payload: CouponEditorConfigType[]
    }
  | {
      type: CustomizeUIActionSetType.SET_PAYMENTS_METHODS_DATA
      payload: PaymentMethodConfigType[]
    }
  | {
      type: CustomizeUIActionSetType.SET_BRAND_LOGO_URL_DATA
      payload: string
    }
  | {
      type: CustomizeUIActionSetType.SET_COLOR_PALETTE_DATA
      payload: ThemeColorPaletteType
    }
  | {
      type: CustomizeUIActionSetType.SET_BANNER_CONFIG_DATA
      payload: {
        bannerConfig: BannerConfigType
        type: CheckoutPageType
      }
    }
  | {
      type: CustomizeUIActionSetType.SET_BUTTON_CTA_TEXT_DATA
      payload: {
        CTAText: string
        type: CTAButtonPageType
      }
    }
  | {
      type: CustomizeUIActionSetType.SET_COUPON_VISIBILITY_CONFIG_DATA
      payload: {
        couponVisibilityConfig: CouponVisibilityConfigType
        type: CheckoutPageType
      }
    }
  | {
      type: CustomizeUIActionSetType.SET_DELIVERY_INFO_CONFIG_DATA
      payload: DeliveryInfoConfigType
    }
  | {
      type: CustomizeUIActionSetType.SET_TRUST_BADGES_CONFIG_DATA
      payload: TrustBadgesConfigType
    }
  | {
      type: CustomizeUIActionSetType.SET_ORDER_SUMMARY_CONFIG
      payload: OrderSummaryConfigType
    }
  | {
      type: CustomizeUIActionSetType.SET_BANK_OFFERS_CONFIG_DATA
      payload: boolean
    }
  | {
      type: CustomizeUIActionSetType.SET_COMPLETE_CONFIG
      payload: CustomizeUIStateType
    }
  | {
      type: CustomizeUIActionSetType.SET_ETD_PROVIDERS_DATA
      payload: EtdProviderType[]
    }
  | {
      type: CustomizeUIActionSetType.SET_THANK_YOU_PAGE_FEATURE_STATE
      payload: FeatureState
    }
  | {
      type: CustomizeUIActionSetType.SET_ADDRESS_CONFIG_DATA
      payload: AddressConfigType
    }
  | {
      type: CustomizeUIActionSetType.SET_MARKETING_FLAG_CONFIG_DATA
      payload: MarketingFlagConfigType
    }
  | {
      type: CustomizeUIActionSetType.SET_DIALOG_DISPLAY_CONFIG_DATA
      payload: DialogDisplayConfigType[]
    }
export type CustomizeUIStateType = {
  pageContext: CustomizeUIPageContextType
  tabList: SwitchableTabType[]
  brandLogoURL: string
  colorPalette: ThemeColorPaletteType
  bannerConfigs: {
    loginBanner: BannerConfigType
    addressBanner: BannerConfigType
    paymentBanner: BannerConfigType
  }
  buttonCTATexts: {
    loginButtonCTAText: string
    addressButtonCTAText: string
  }
  couponVisibilityConfig: {
    loginCouponVisibility: CouponVisibilityConfigType
    addressCouponVisibility: CouponVisibilityConfigType
    paymentsCouponVisibility: CouponVisibilityConfigType
  }
  deliveryInfoConfig: DeliveryInfoConfigType
  paymentMethodsConfig: PaymentMethodConfigType[]
  couponEditorConfig: CouponEditorConfigType[]
  trustBadgesConfig: TrustBadgesConfigType
  orderSummaryConfig: OrderSummaryConfigType
  showBankOffers: boolean
  etdProviders: EtdProviderType[]
  thankYouPageConfig: ThankYouPageConfigType
  addressConfig?: AddressConfigType
  marketingFlagConfig: MarketingFlagConfigType
  dialogDisplayConfig: DialogDisplayConfigType[]
}

export type ThankYouPageConfigType = {
  mainFeatureState: FeatureState
}

export type ThemeColorPaletteType = {
  primaryColor: string
  primaryTextColor: string
}

export type BannerConfigType = {
  color: string
  text: string
  textColor: string
  enabled: boolean
  multipleBannersEnabled?: boolean
  animationStyle?: string
  animationDelay?: number
  banners?: BannerItemConfigType[]
}

export type BannerItemConfigType = {
  id: string
  position: number
  color: string
  text: string
  textColor: string
}

export type CustomizeUIPagesType = 'BRAND' | 'ELEMENTS' | 'LOGIN' | 'COUPONS' | 'ADDRESS' | 'PAYMENT'
export type CheckoutPageType = 'PAYMENTS' | 'ORDER_SUMMARY' | 'AUTH'

export type CTAButtonPageType = 'ORDER_SUMMARY' | 'AUTH'

export type CouponVisibilityConfigType = {
  hideSavings: boolean
  areCouponsVisible: boolean
  visibilityExtent: CouponVisibilityExtentType
}

export type CouponVisibilityExtentType = 'cards' | 'codeEntry'

export type DeliveryInfoConfigType = {
  isVisible: boolean
  deliveryInfoText: string
  isETDEnabled?: boolean
}

export type PaymentMethodConfigType = Record<string, any>

export type CouponEditorConfigType = DiscountTableDataType

export type TrustBadgesConfigType = {
  badges: SelectedTrustBadgeType[]
  showBadges: boolean
}

export type SelectedTrustBadgeType = {
  label: string
  imgPath?: string
  text?: string
  weight?: number | null
  isActive?: boolean
}

export type OrderSummaryConfigType = {
  isOrderSummaryOpen: boolean
  isQuantityEditable: boolean
  isPriceStrikeThroughEnabled: boolean
  showOrderSummaryAs: OrderSummaryStyleType
  isOnePageCheckoutEnable: boolean
}

export type OrderSummaryStyleType = 'TILE' | 'HEADER'

export type MarketingFlagConfigType = {
  enableMarketingFlag: boolean
  marketingConsentText: string
}

export type DialogDisplayConfigType = {
  key: string
  delay: number
  weight: number
  enabled: boolean
  duration: number
  metadata: Record<string, any>
}
